import { useState } from 'react';
import { ChevronsUpDown, X } from 'lucide-react';
import type { PolicyDestinationAddressEntry } from '@mls-wallet/shared-types';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { AddressDisplay } from '@/components/shared/address-display';
import { AddressBookSelectorDialog } from '@/components/shared/address-book-selector-dialog';
import { getAddressSelectorTypeLabel } from '@/components/shared/address-selector';

export type DestinationAddressSelectorValue =
  | { type: 'any' }
  | {
      type: 'custom';
      addresses: string[];
      entries?: PolicyDestinationAddressEntry[];
    };

type DestinationSelectionEntry = {
  address: string;
  name: string | null;
  type: PolicyDestinationAddressEntry['type'] | null;
};

type DestinationAddressSelectorProps = {
  value: DestinationAddressSelectorValue;
  onValueChange: (value: DestinationAddressSelectorValue) => void;
};

function createFallbackEntry(address: string): DestinationSelectionEntry {
  // Preserve raw addresses even when we do not yet have label/type metadata.
  return {
    address,
    name: null,
    type: null,
  };
}

function getInitialCustomEntries(
  value: DestinationAddressSelectorValue
): DestinationSelectionEntry[] {
  // Case: "any destination" has no staged custom entries.
  if (value.type !== 'custom') {
    return [];
  }

  const existingEntriesByAddress = new Map(
    (value.entries ?? []).map((entry) => [entry.address.toLowerCase(), entry])
  );

  return value.addresses.map(
    (address) =>
      existingEntriesByAddress.get(address.toLowerCase()) ??
      createFallbackEntry(address)
  );
}

export function DestinationAddressSelector({
  value,
  onValueChange,
}: DestinationAddressSelectorProps) {
  const [open, setOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [tempType, setTempType] = useState<'any' | 'custom'>(
    value.type === 'custom' ? 'custom' : 'any'
  );
  const [tempSelectedEntries, setTempSelectedEntries] = useState<
    DestinationSelectionEntry[]
  >(value.type === 'custom' ? getInitialCustomEntries(value) : []);
  const [showSelectionRequiredError, setShowSelectionRequiredError] =
    useState(false);

  const getDisplayValue = () => {
    // Case: unrestricted destination policy.
    if (value.type === 'any') {
      return 'Any destination';
    }

    const count = value.addresses.filter(Boolean).length;

    // Case: the custom list exists but is currently empty.
    if (count === 0) {
      return 'Custom list of destination addresses';
    }

    return `${count} destination address${count !== 1 ? 'es' : ''}`;
  };

  const resetTempState = () => {
    // Reset the dialog draft back to the last saved prop value.
    setTempType(value.type === 'custom' ? 'custom' : 'any');
    setTempSelectedEntries(getInitialCustomEntries(value));
    setShowSelectionRequiredError(false);
    setPickerOpen(false);
  };

  const handleOpen = () => {
    resetTempState();
    setOpen(true);
  };

  const handleCancel = () => {
    resetTempState();
    setOpen(false);
  };

  const handleRemoveAddress = (address: string) => {
    // Remove a single staged destination from the custom list draft.
    setTempSelectedEntries((currentEntries) =>
      currentEntries.filter((entry) => entry.address !== address)
    );
  };

  const handleSave = () => {
    // Case: custom mode requires at least one selected destination.
    if (tempType === 'custom' && tempSelectedEntries.length === 0) {
      setShowSelectionRequiredError(true);
      return;
    }

    // Case: persist the staged custom list back into the policy form.
    if (tempType === 'custom') {
      const persistedEntries = tempSelectedEntries.flatMap((entry) =>
        entry.name && entry.type
          ? [
              {
                address: entry.address,
                name: entry.name,
                type: entry.type,
              } satisfies PolicyDestinationAddressEntry,
            ]
          : []
      );

      onValueChange({
        type: 'custom',
        addresses: tempSelectedEntries.map((entry) => entry.address),
        ...(persistedEntries.length > 0 ? { entries: persistedEntries } : {}),
      });
    } else {
      // Case: switching back to "any destination" clears the custom list.
      onValueChange({ type: 'any' });
    }

    setOpen(false);
  };

  return (
    <>
      <Button
        variant="outline"
        role="combobox"
        aria-expanded={open}
        size="lg"
        className={cn(
          'w-full justify-between bg-background border-slate-300 border',
          'text-base'
        )}
        onClick={handleOpen}
      >
        <span className="flex items-center gap-2">{getDisplayValue()}</span>
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>

      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            handleCancel();
            return;
          }

          setOpen(true);
        }}
      >
        <DialogContent className="sm:max-w-[720px] [&>button]:hidden">
          <DialogHeader className="space-y-2">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-2">
                <DialogTitle className="text-2xl tracking-[-0.03em]">
                  Select destination addresses
                </DialogTitle>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-9 w-9 rounded-full text-zinc-500"
                onClick={handleCancel}
              >
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </Button>
            </div>
          </DialogHeader>

          <div className="space-y-6">
            <RadioGroup
              value={tempType}
              onValueChange={(type) =>
                setTempType(type === 'any' ? 'any' : 'custom')
              }
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="any" id="any-destination" />
                <Label htmlFor="any-destination">Any destination</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="custom" id="custom-destination" />
                <Label htmlFor="custom-destination">
                  Custom list of destination addresses
                </Label>
              </div>
            </RadioGroup>

            {tempType === 'custom' ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Addresses</Label>
                  <div className="overflow-hidden rounded-[24px] border border-zinc-200 bg-zinc-50">
                    {tempSelectedEntries.length === 0 ? (
                      <div className="px-6 py-7">
                        <p className="text-[1.75rem] font-medium tracking-[-0.04em] text-zinc-950">
                          No destination addresses added
                        </p>
                        <p className="mt-2 text-lg text-zinc-500">
                          Add a destination address
                        </p>
                      </div>
                    ) : (
                      <div className="max-h-[360px] overflow-y-auto divide-y divide-zinc-100">
                        {tempSelectedEntries.map((entry) => (
                          <div
                            key={entry.address}
                            className="flex items-center justify-between gap-4 px-6 py-4"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <AddressDisplay
                                  address={entry.address}
                                  name={entry.name}
                                  showAddressName
                                />
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              {getAddressSelectorTypeLabel(entry.type) ? (
                                <p className="hidden text-sm text-zinc-500 sm:block">
                                  {getAddressSelectorTypeLabel(entry.type)}
                                </p>
                              ) : null}
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() =>
                                  handleRemoveAddress(entry.address)
                                }
                              >
                                <X className="h-4 w-4" />
                                <span className="sr-only">
                                  Remove destination address
                                </span>
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {showSelectionRequiredError ? (
                    <p className="px-1 text-sm text-red-600">
                      Add at least one destination address to use a custom list.
                    </p>
                  ) : null}
                </div>

                <Button
                  type="button"
                  className="w-full"
                  onClick={() => setPickerOpen(true)}
                >
                  Add destination address
                </Button>
              </div>
            ) : null}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={
                tempType === 'custom' && tempSelectedEntries.length === 0
              }
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AddressBookSelectorDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        mode="multiple"
        title="Add destination address"
        description="Choose addresses from the address book, or save a named custom address to it."
        selectedAddresses={tempSelectedEntries.map((entry) => entry.address)}
        onConfirmSelection={({ entries }) => {
          // Replace the staged destination list with the selector's confirmed set.
          setTempSelectedEntries(entries);
          setShowSelectionRequiredError(false);
          setPickerOpen(false);
        }}
      />
    </>
  );
}
